import { useState, useEffect } from "react";
import {
  Key, Plus, ToggleLeft, ToggleRight,
  Copy, Check, RefreshCw, X, AlertTriangle, Building2, Pencil
} from "lucide-react";
import DashboardLayout from "../../../components/DashboardLayout";
import { LayoutDashboard, Users, ScrollText } from "lucide-react";
import { supabase } from "../../../lib/supabaseClient";

// const NAV = [
//   { label: "Dashboard",     icon: LayoutDashboard },
//   { label: "Properties",    icon: Building2 },
//   { label: "Users",         icon: Users },
//   { label: "Valet Codes",   icon: Key, active: true },
//   { label: "Activity Logs", icon: ScrollText },
//   { label: "Settings",      icon: Settings },
// ];

const NAV = [
  { label: "Dashboard",     icon: LayoutDashboard, path: "/admin/dashboard" },
  { label: "Properties",    icon: Building2,        path: "/admin/properties" },
  { label: "Users",         icon: Users,            path: "/admin/users" },
  { label: "Valet Codes",   icon: Key,              path: "/admin/valet-codes" },
  { label: "Activity Logs", icon: ScrollText,       path: "/admin/logs" },
  // { label: "Settings",      icon: Settings,         path: "/admin/settings" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function genValetCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "VLT-";
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button onClick={copy} style={{
      background: "none", border: "none", cursor: "pointer",
      color: copied ? "var(--g600)" : "var(--mut)", padding: "2px 4px",
      borderRadius: 4, display: "inline-flex", alignItems: "center",
    }}>
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

function Badge({ active }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase",
      padding: "3px 10px", borderRadius: 20,
      background: active ? "var(--g50)" : "#fef2f2",
      color: active ? "var(--g600)" : "#dc2626",
      border: `1px solid ${active ? "var(--g200)" : "#fecaca"}`,
    }}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ title, message, confirmLabel, confirmColor = "#dc2626", onConfirm, onCancel }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.4)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1100, padding: 16,
    }}>
      <div style={{
        background: "#fff", borderRadius: 14, padding: 24,
        width: "100%", maxWidth: 360,
        boxShadow: "0 8px 40px rgba(0,0,0,.16)",
        animation: "fadeUp .18s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, background: "#fef2f2",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <AlertTriangle size={18} color="#dc2626" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{title}</div>
        </div>
        <div style={{ fontSize: 13, color: "var(--mut)", lineHeight: 1.6, marginBottom: 20 }}>{message}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: "10px", borderRadius: 9, border: "1.5px solid var(--ln)",
            background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--mut)",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            flex: 1, padding: "10px", borderRadius: 9, border: "none",
            background: confirmColor, cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#fff",
          }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Label Modal ─────────────────────────────────────────────────────────
function EditLabelModal({ vc, onClose, onSaved }) {
  const [label, setLabel]     = useState(vc.label || "");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [apiErr, setApiErr]   = useState("");

  const submit = async () => {
    if (!label.trim()) { setError("Label required"); return; }
    setLoading(true); setApiErr("");
    const { error } = await supabase
      .from("valet_codes")
      .update({ label: label.trim() })
      .eq("id", vc.id);
    setLoading(false);
    if (error) { setApiErr(error.message); return; }
    onSaved();
    onClose();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: 28,
        width: "100%", maxWidth: 400,
        boxShadow: "0 8px 40px rgba(0,0,0,.14)",
        animation: "fadeUp .2s ease",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#6a1b9a", marginBottom: 4 }}>Super Admin</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>Edit Valet Label</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--mut)", padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Code display (read-only) */}
        <div style={{
          background: "var(--g50)", border: "1.5px solid var(--g200)", borderRadius: 10,
          padding: "10px 14px", marginBottom: 16,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <Key size={14} color="var(--g600)" />
          <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14, letterSpacing: "1.5px", color: "var(--g700)" }}>
            {vc.code}
          </span>
          <span style={{ fontSize: 12, color: "var(--mut)", marginLeft: 4 }}>— code cannot be changed</span>
        </div>

        {apiErr && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 14 }}>
            {apiErr}
          </div>
        )}

        {/* Label Field */}
        <div style={{ marginBottom: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>
            Label *
          </label>
          <input
            type="text"
            value={label}
            onChange={e => { setLabel(e.target.value); setError(""); }}
            placeholder="e.g. Driver A, Night Shift"
            autoFocus
            onKeyDown={e => e.key === "Enter" && submit()}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13,
              border: `1.5px solid ${error ? "#fca5a5" : "var(--ln)"}`,
              outline: "none", background: "#fafafa", color: "var(--ink)", boxSizing: "border-box",
            }}
          />
          {error && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{error}</div>}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "11px", borderRadius: 10, border: "1.5px solid var(--ln)",
            background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--mut)",
          }}>Cancel</button>
          <button onClick={submit} disabled={loading} style={{
            flex: 2, padding: "11px", borderRadius: 10, border: "none",
            background: loading ? "var(--g300)" : "var(--g600)",
            cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, color: "#fff",
          }}>
            {loading ? "Saving…" : "Save Label"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Valet Code Modal ─────────────────────────────────────────────────────
function AddCodeModal({ properties, onClose, onSaved }) {
  const [f, setF] = useState({ property_id: "", label: "", code: genValetCode() });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [apiErr, setApiErr]   = useState("");

  const upd = k => v => { setF(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: "" })); };
  const regen = () => setF(p => ({ ...p, code: genValetCode() }));

  const validate = () => {
    const e = {};
    if (!f.property_id)   e.property_id = "Select a property";
    if (!f.label.trim())  e.label       = "Label required (e.g. Driver A)";
    return e;
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true); setApiErr("");
    const { error } = await supabase.from("valet_codes").insert([{
      property_id: f.property_id,
      label:       f.label.trim(),
      code:        f.code,
      is_active:   true,
    }]);
    setLoading(false);
    if (error) { setApiErr(error.message); return; }
    onSaved(); onClose();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: 28,
        width: "100%", maxWidth: 440,
        boxShadow: "0 8px 40px rgba(0,0,0,.14)",
        animation: "fadeUp .2s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#6a1b9a", marginBottom: 4 }}>Super Admin</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>Generate Valet Code</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--mut)", padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {apiErr && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 16 }}>
            {apiErr}
          </div>
        )}

        {/* Property */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>Property *</label>
          <select value={f.property_id} onChange={e => upd("property_id")(e.target.value)} style={{
            width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13,
            border: `1.5px solid ${errors.property_id ? "#fca5a5" : "var(--ln)"}`,
            outline: "none", background: "#fafafa", color: "var(--ink)", boxSizing: "border-box",
          }}>
            <option value="">— Select property —</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {errors.property_id && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{errors.property_id}</div>}
        </div>

        {/* Label */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>
            Label * <span style={{ fontWeight: 400, color: "var(--mut)" }}>(e.g. Driver A, Night Shift)</span>
          </label>
          <input type="text" value={f.label} onChange={e => upd("label")(e.target.value)} placeholder="e.g. Driver A"
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13,
              border: `1.5px solid ${errors.label ? "#fca5a5" : "var(--ln)"}`,
              outline: "none", background: "#fafafa", color: "var(--ink)", boxSizing: "border-box",
            }}
          />
          {errors.label && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{errors.label}</div>}
        </div>

        {/* Generated Code */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>
            Valet Code <span style={{ fontWeight: 400, color: "var(--mut)" }}>(auto-generated)</span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              flex: 1, padding: "10px 12px", borderRadius: 8, fontSize: 14,
              border: "1.5px solid var(--ln)", background: "var(--g50)",
              color: "var(--g700)", fontWeight: 700, letterSpacing: "2px", fontFamily: "monospace",
            }}>{f.code}</div>
            <button onClick={regen} title="Regenerate" style={{
              padding: "9px 11px", borderRadius: 8, border: "1.5px solid var(--ln)",
              background: "#fff", cursor: "pointer", color: "var(--mut)", display: "flex", alignItems: "center",
            }}><RefreshCw size={14} /></button>
            <CopyBtn value={f.code} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "11px", borderRadius: 10, border: "1.5px solid var(--ln)",
            background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--mut)",
          }}>Cancel</button>
          <button onClick={submit} disabled={loading} style={{
            flex: 2, padding: "11px", borderRadius: 10, border: "none",
            background: loading ? "var(--g300)" : "var(--g600)",
            cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, color: "#fff",
          }}>{loading ? "Generating…" : "Generate Code"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Valet Code Row ───────────────────────────────────────────────────────────
function CodeRow({ vc, propertyName, onToggle, onEdit }) {
  const [toggling, setToggling]           = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(false);

  const handleConfirmed = async () => {
    setConfirmToggle(false);
    setToggling(true);
    await onToggle(vc.id, !vc.is_active);
    setToggling(false);
  };

  return (
    <>
      <div style={{
        background: "#fff", borderRadius: 12,
        border: `1.5px solid ${vc.is_active ? "var(--ln)" : "#fecaca"}`,
        marginBottom: 8, padding: "13px 16px",
        display: "flex", alignItems: "center", gap: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,.04)",
        opacity: vc.is_active ? 1 : 0.75,
        transition: "opacity .2s",
      }}>
        {/* Icon */}
        <div style={{
          width: 34, height: 34, borderRadius: 9,
          background: vc.is_active ? "var(--g50)" : "#fef2f2",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Key size={15} color={vc.is_active ? "var(--g600)" : "#dc2626"} />
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "monospace", color: "var(--ink)", letterSpacing: "1px" }}>
              {vc.code}
            </span>
            <CopyBtn value={vc.code} />
          </div>
          <div style={{ fontSize: 12, color: "var(--mut)" }}>
            <span style={{ fontWeight: 600, color: "var(--ink)" }}>{vc.label}</span>
            {" · "}<span>{propertyName}</span>
            {vc.last_used_at && <span> · Last used: {new Date(vc.last_used_at).toLocaleDateString()}</span>}
          </div>
        </div>

        <Badge active={vc.is_active} />

        {/* Edit Button */}
        <button
          onClick={() => onEdit(vc)}
          title="Edit label"
          style={{
            background: "none", border: "1.5px solid var(--ln)", borderRadius: 8,
            cursor: "pointer", color: "var(--mut)", padding: "5px 8px",
            display: "flex", alignItems: "center", flexShrink: 0,
          }}
        >
          <Pencil size={14} />
        </button>

        {/* Toggle */}
        <button
          onClick={() => setConfirmToggle(true)}
          disabled={toggling}
          title={vc.is_active ? "Deactivate" : "Activate"}
          style={{
            background: "none", border: "none",
            cursor: toggling ? "not-allowed" : "pointer",
            color: vc.is_active ? "var(--g600)" : "#dc2626", padding: "2px 4px", flexShrink: 0,
          }}
        >
          {toggling
            ? <RefreshCw size={17} style={{ animation: "spin 1s linear infinite" }} />
            : vc.is_active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />
          }
        </button>
      </div>

      {confirmToggle && (
        <ConfirmDialog
          title={vc.is_active ? "Deactivate Valet Code?" : "Activate Valet Code?"}
          message={
            vc.is_active
              ? `Code "${vc.code}" (${vc.label}) will be deactivated. This valet will lose access immediately.`
              : `Code "${vc.code}" (${vc.label}) will be reactivated.`
          }
          confirmLabel={vc.is_active ? "Deactivate" : "Activate"}
          confirmColor={vc.is_active ? "#dc2626" : "var(--g600)"}
          onConfirm={handleConfirmed}
          onCancel={() => setConfirmToggle(false)}
        />
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SuperAdminValetCodes() {
  const [codes, setCodes]           = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [editVc, setEditVc]         = useState(null);
  const [filterProp, setFilterProp]     = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchData = async () => {
    setLoading(true);
    const [{ data: props }, { data: vcs }] = await Promise.all([
      supabase.from("properties").select("id, name").order("name"),
      supabase.from("valet_codes").select("*").order("created_at", { ascending: false }),
    ]);
    setProperties(props || []);
    setCodes(vcs || []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleToggle = async (id, newStatus) => {
    await supabase.from("valet_codes").update({ is_active: newStatus }).eq("id", id);
    fetchData();
  };

  const propMap = Object.fromEntries((properties || []).map(p => [p.id, p.name]));

  const filtered = codes.filter(vc => {
    const matchProp   = filterProp === "all"   || vc.property_id === filterProp;
    const matchStatus = filterStatus === "all" || (filterStatus === "active" ? vc.is_active : !vc.is_active);
    return matchProp && matchStatus;
  });

  const grouped = filtered.reduce((acc, vc) => {
    const key = vc.property_id || "unassigned";
    if (!acc[key]) acc[key] = [];
    acc[key].push(vc);
    return acc;
  }, {});

  return (
    <DashboardLayout role="super_admin" title="Valet Code Management" navItems={NAV}>

      {/* Page Header */}
      <div className="fu" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#6a1b9a", marginBottom: 6 }}>Super Admin</div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 500, color: "var(--ink)", margin: 0 }}>Valet Codes</h2>
            <p style={{ fontSize: 13, color: "var(--mut)", marginTop: 4 }}>
              {codes.length} total · {codes.filter(c => c.is_active).length} active
            </p>
          </div>
          <button onClick={() => setShowModal(true)} style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "10px 18px", borderRadius: 10, border: "none",
            background: "var(--g600)", color: "#fff",
            fontSize: 13, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 2px 8px rgba(34,107,65,.25)",
          }}>
            <Plus size={15} /> Generate Code
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="fu d1" style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <select value={filterProp} onChange={e => setFilterProp(e.target.value)} style={{
          padding: "9px 13px", borderRadius: 8, fontSize: 13,
          border: "1.5px solid var(--ln)", background: "#fafafa",
          color: "var(--ink)", outline: "none", minWidth: 160,
        }}>
          <option value="all">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        {["all", "active", "inactive"].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            padding: "9px 16px", borderRadius: 8, border: "1.5px solid var(--ln)",
            background: filterStatus === s ? "var(--g600)" : "#fff",
            color: filterStatus === s ? "#fff" : "var(--mut)",
            fontSize: 12, fontWeight: 700, cursor: "pointer", textTransform: "capitalize",
          }}>{s}</button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--mut)", fontSize: 13 }}>Loading valet codes…</div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "48px 24px",
          background: "#fff", borderRadius: 14, border: "1.5px dashed var(--ln)",
        }}>
          <Key size={32} color="var(--ln)" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 6 }}>No valet codes found</div>
          <div style={{ fontSize: 13, color: "var(--mut)" }}>Click 'Generate Code' to create the first one.</div>
        </div>
      ) : (
        Object.entries(grouped).map(([propId, propCodes]) => (
          <div key={propId} style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Building2 size={14} color="var(--mut)" />
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "var(--mut)" }}>
                {propMap[propId] || "Unknown Property"}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                background: "var(--g50)", color: "var(--g600)", border: "1px solid var(--g200)",
              }}>
                {propCodes.length} code{propCodes.length !== 1 ? "s" : ""}
              </span>
            </div>

            {propCodes.map(vc => (
              <CodeRow
                key={vc.id}
                vc={vc}
                propertyName={propMap[vc.property_id] || "—"}
                onToggle={handleToggle}
                onEdit={setEditVc}
              />
            ))}
          </div>
        ))
      )}

      {/* Add Modal */}
      {showModal && (
        <AddCodeModal properties={properties} onClose={() => setShowModal(false)} onSaved={fetchData} />
      )}

      {/* Edit Label Modal */}
      {editVc && (
        <EditLabelModal vc={editVc} onClose={() => setEditVc(null)} onSaved={fetchData} />
      )}

      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin   { to { transform:rotate(360deg); } }
      `}</style>

    </DashboardLayout>
  );
}